import { z } from 'zod';
import { CurrencyCodeSchema, InvoiceLineItemSourceTypeSchema, InvoicePaymentStatusSchema, InvoiceStatusSchema, PricingModelSchema, StringFilterOperatorSchema, } from '../common/enums.js';
import { MetadataSchema, PaginationMetaSchema, PaginationQuerySchema, } from '../common/types.js';
export { InvoiceLineItemSourceTypeSchema, InvoicePaymentStatusSchema, InvoiceStatusSchema, } from '../common/enums.js';
export const InvoiceTaxSchema = z.object({
    id: z.string().optional(),
    invoice_id: z.string().optional(),
    tax_id: z.string().nullable().optional(),
    tax_name: z.string(),
    tax_rate: z.number(),
    taxable_amount_cents: z.number().int(),
    tax_amount_cents: z.number().int(),
    created_at: z.string().datetime().optional(),
});
export const CreateInvoiceTaxSchema = z.object({
    tax_id: z.string().optional(),
    tax_name: z.string(),
    tax_rate: z.number().min(0).max(1),
    taxable_amount_cents: z.number().int().min(0),
    tax_amount_cents: z.number().int().min(0),
});
export const InvoiceLineItemSchema = z.object({
    id: z.string().optional(),
    invoice_id: z.string().optional(),
    contract_id: z.string().nullable().optional(),
    source_type: InvoiceLineItemSourceTypeSchema.nullable().optional(),
    source_id: z.string().nullable().optional(),
    agent_id: z.string().nullable().optional(),
    feature_id: z.string().nullable().optional(),
    signal_id: z.string().nullable().optional(),
    description: z.string(),
    pricing_model: PricingModelSchema.nullable().optional(),
    period_from: z.string().datetime().nullable().optional(),
    period_to: z.string().datetime().nullable().optional(),
    quantity: z.number(),
    included_units_used: z.number().default(0).optional(),
    billable_units: z.number().optional(),
    unit_price_cents: z.number().int(),
    tier_number: z.number().int().nullable().optional(),
    subtotal_cents: z.number().int(),
    discount_cents: z.number().int().default(0).optional(),
    tax_cents: z.number().int().default(0).optional(),
    total_cents: z.number().int(),
    sort_order: z.number().int().default(0).optional(),
    created_at: z.string().datetime().optional(),
});
export const CreateInvoiceLineItemSchema = z.object({
    contract_id: z.string().optional(),
    source_type: InvoiceLineItemSourceTypeSchema.optional(),
    source_id: z.string().optional(),
    agent_id: z.string().optional(),
    feature_id: z.string().optional(),
    signal_id: z.string().optional(),
    description: z.string().min(1).max(500),
    pricing_model: PricingModelSchema.optional(),
    period_from: z.string().datetime().optional(),
    period_to: z.string().datetime().optional(),
    quantity: z.number().min(0),
    included_units_used: z.number().min(0).default(0).optional(),
    billable_units: z.number().min(0).optional(),
    unit_price_cents: z.number().int().min(0),
    tier_number: z.number().int().optional(),
    subtotal_cents: z.number().int().min(0),
    discount_cents: z.number().int().min(0).default(0).optional(),
    tax_cents: z.number().int().min(0).default(0).optional(),
    total_cents: z.number().int().min(0),
    sort_order: z.number().int().default(0).optional(),
});
export const InvoiceSchema = z.object({
    id: z.string(),
    organization_id: z.string().nullable().optional(),
    customer_id: z.string(),
    contract_id: z.string().nullable().optional(),
    invoice_number: z.string(),
    issue_date: z.string().datetime(),
    due_date: z.string().datetime().nullable().optional(),
    billing_period_from: z.string().datetime().nullable().optional(),
    billing_period_to: z.string().datetime().nullable().optional(),
    currency: CurrencyCodeSchema.default('USD'),
    status: InvoiceStatusSchema,
    payment_status: InvoicePaymentStatusSchema,
    subtotal_cents: z.number().int(),
    tax_cents: z.number().int(),
    discount_cents: z.number().int(),
    total_cents: z.number().int(),
    amount_paid_cents: z.number().int(),
    amount_due_cents: z.number().int(),
    tax_rate: z.number().nullable().optional(),
    notes: z.string().nullable().optional(),
    metadata: MetadataSchema.optional(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    line_items: z.array(InvoiceLineItemSchema).optional(),
    taxes: z.array(InvoiceTaxSchema).optional(),
    customer: z
        .object({
        id: z.string(),
        name: z.string(),
        email: z.string().nullable().optional(),
    })
        .optional(),
});
export const CreateInvoiceSchema = z.object({
    customer_id: z.string().min(1),
    contract_id: z.string().optional(),
    issue_date: z.string().datetime(),
    due_date: z.string().datetime().optional(),
    billing_period_from: z.string().datetime().optional(),
    billing_period_to: z.string().datetime().optional(),
    currency: CurrencyCodeSchema.default('USD').optional(),
    tax_rate: z.number().min(0).max(1).optional(),
    notes: z.string().max(2000).optional(),
    line_items: z.array(CreateInvoiceLineItemSchema).min(1),
    taxes: z.array(CreateInvoiceTaxSchema).optional(),
    auto_finalize: z.boolean().default(false).optional(),
    metadata: MetadataSchema.optional(),
});
export const UpdateInvoiceSchema = z.object({
    issue_date: z.string().datetime().optional(),
    due_date: z.string().datetime().optional(),
    billing_period_from: z.string().datetime().optional(),
    billing_period_to: z.string().datetime().optional(),
    currency: CurrencyCodeSchema.optional(),
    tax_rate: z.number().min(0).max(1).optional(),
    notes: z.string().max(2000).optional(),
    line_items: z.array(CreateInvoiceLineItemSchema).optional(),
    taxes: z.array(CreateInvoiceTaxSchema).optional(),
    metadata: MetadataSchema.optional(),
});
export const FinalizeInvoiceSchema = z.object({
    send_email: z.boolean().default(false).optional(),
});
export const VoidInvoiceSchema = z.object({
    reason: z.string().min(1).max(500),
});
export const ApproveInvoiceSchema = z.object({
    approved_by: z.string().optional(),
    notes: z.string().max(500).optional(),
});
export const UpdateInvoiceStatusSchema = z.object({
    status: InvoiceStatusSchema,
    reason: z.string().max(500).optional(),
});
export const UpdateInvoicePaymentStatusSchema = z.object({
    payment_status: InvoicePaymentStatusSchema,
    amount_paid_cents: z.number().int().min(0).optional(),
    notes: z.string().max(500).optional(),
});
export const InvoiceQuerySchema = PaginationQuerySchema.extend({
    search: z.string().optional(),
    customer_id: z.string().optional(),
    contract_id: z.string().optional(),
    status: InvoiceStatusSchema.optional(),
    payment_status: InvoicePaymentStatusSchema.optional(),
    currency: CurrencyCodeSchema.optional(),
    issue_date_from: z.string().datetime().optional(),
    issue_date_to: z.string().datetime().optional(),
    due_date_from: z.string().datetime().optional(),
    due_date_to: z.string().datetime().optional(),
    min_amount_cents: z.coerce.number().int().optional(),
    max_amount_cents: z.coerce.number().int().optional(),
    sort: z
        .enum([
        'invoice_number',
        'issue_date',
        'due_date',
        'total_cents',
        'status',
        'created_at',
    ])
        .default('created_at')
        .optional(),
    filter_field: z
        .enum(['customer_name', 'invoice_number', 'organization_name'])
        .optional(),
    filter_operator: StringFilterOperatorSchema.optional(),
    filter_value: z.string().optional(),
});
export const InvoiceResponseSchema = z.object({
    data: InvoiceSchema,
});
export const PaginatedInvoicesResponseSchema = z.object({
    data: z.array(InvoiceSchema),
    pagination: PaginationMetaSchema,
    summary: z
        .object({
        total_count: z.number().int(),
        total_revenue_cents: z.number().int(),
        outstanding_cents: z.number().int(),
        overdue_count: z.number().int(),
        total_amount_cents: z.number().int(),
        total_paid_cents: z.number().int(),
        total_due_cents: z.number().int(),
        by_status: z.record(z.string(), z.number().int()).optional(),
        by_payment_status: z.record(z.string(), z.number().int()).optional(),
    })
        .optional(),
});
export const InvoiceParamsSchema = z.object({
    id: z.string(),
});
export const InvoiceLineItemParamsSchema = z.object({
    id: z.string(),
    lineItemId: z.string(),
});
export const InvoicePreviewSchema = z.object({
    customer_id: z.string().min(1),
    contract_id: z.string().optional(),
    billing_period_from: z.string().datetime(),
    billing_period_to: z.string().datetime(),
    include_usage: z.boolean().default(true).optional(),
});
export const InvoicePreviewResponseSchema = z.object({
    customer_id: z.string(),
    contract_id: z.string().nullable(),
    billing_period_from: z.string().datetime(),
    billing_period_to: z.string().datetime(),
    currency: CurrencyCodeSchema,
    line_items: z.array(z.object({
        description: z.string(),
        agent_id: z.string().nullable(),
        feature_id: z.string().nullable(),
        signal_id: z.string().nullable(),
        quantity: z.number(),
        unit_price_cents: z.number().int(),
        subtotal_cents: z.number().int(),
    })),
    subtotal_cents: z.number().int(),
    tax_cents: z.number().int(),
    total_cents: z.number().int(),
});
export const InvoiceActionResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    id: z.string(),
    status: InvoiceStatusSchema,
    invoice_number: z.string().optional(),
    finalized_at: z.string().datetime().optional(),
    voided_at: z.string().datetime().optional(),
    void_reason: z.string().optional(),
});
//# sourceMappingURL=index.js.map